import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQuery } from '@/core/api/baseQuery'
import { GenericResponseOData } from '@/core/api/interfaces/GenericResponseOData'
import {
  AgenteOperativo,
  AsignacionEjecutivoOperativo,
  EjecutivoComercial,
} from '@/core/odata/interfaces/MtPerseveranceBackendApiRepositoriesModel'

export interface EjecutivoComercialAsignacion extends Pick<
  EjecutivoComercial,
  | 'EjecutivoComercialId'
  | 'Nombre'
  | 'Apellido'
  | 'CorreoElectronico'
  | 'Activo'
  | 'Identificador'
  | 'Iniciales'
  | 'NombreCompleto'
> {
  AsignacionEjecutivoOperativo?: Array<
    Pick<
      AsignacionEjecutivoOperativo,
      'AsignacionEjecutivoOperativoId' | 'FechaUltimaModificacion' | 'UsuarioUltimaModificacion'
    > & {
      AgenteOperativo?: Pick<AgenteOperativo, 'AgenteOperativoId' | 'Nombre' | 'Apellido'>
    }
  >
  CantidadAsignaciones?: number
}

export interface AsignarAgenteOperativoPayload {
  EjecutivoComercialId: number
  AgenteOperativoId: number
}

export interface ActualizarAsignacionAgenteOperativoPayload {
  AsignacionEjecutivoOperativoId: number
  EjecutivoComercialId: number
  AgenteOperativoId: number
}

// PutEjecutivoComercial marca toda la entidad como Modified: hay que reenviar Nombre/Apellido/
// Identificador/NombreCompleto tal cual (aunque el grid solo edite Correo y Activo), o el
// backend los sobrescribe con sus valores por defecto (vacío) al guardar.
// UsuarioCreacion/UsuarioUltimaModificacion son `string` no-nullable en la entidad: si se
// omiten, ASP.NET Core rechaza el request con 400 por validación de modelo ANTES de que el
// controller corra (nunca llega a AgregaInfoAuditoriaModificacion, que es quien realmente
// protege/reasigna esos campos) — van como placeholder, el backend los ignora o sobrescribe.
export interface ActualizarEjecutivoComercialPayload {
  EjecutivoComercialId: number
  Nombre: string
  Apellido: string
  CorreoElectronico: string
  Identificador: string
  Iniciales?: string | null
  NombreCompleto: string
  Activo: boolean
  Eliminado: boolean
  UsuarioCreacion: string
  UsuarioUltimaModificacion: string
}

export interface CrearEjecutivoPayload {
  Tipo: string
  Nombre: string
  Apellido: string
  Email: string
  Iniciales?: string
  Cargo?: string
}

export interface AgenteOperativoRegistro
  extends Pick<
    AgenteOperativo,
    'AgenteOperativoId' | 'UsuarioId' | 'Nombre' | 'Apellido' | 'Email' | 'Cargo' | 'Activo'
  > {
  NombreCompleto: string
  FechaUltimaModificacion: string
  UsuarioUltimaModificacion: string
}

// PutAgenteOperativo marca toda la entidad como Modified (no solo los campos enviados),
// así que hay que reenviar UsuarioId/NombreCompleto/Eliminado tal cual, o EF Core los
// sobrescribe con sus valores por defecto (0 / vacío) al guardar.
// UsuarioCreacion/UsuarioUltimaModificacion son `string` no-nullable en la entidad: si se
// omiten, ASP.NET Core rechaza el request con 400 por validación de modelo ANTES de que el
// controller corra (nunca llega a AgregaInfoAuditoriaModificacion, que es quien realmente
// protege/reasigna esos campos) — van como placeholder, el backend los ignora o sobrescribe.
export interface ActualizarAgenteOperativoPayload {
  AgenteOperativoId: number
  UsuarioId: number
  Nombre: string
  Apellido: string
  Email: string
  Cargo: string
  NombreCompleto: string
  Activo: boolean
  Eliminado: boolean
  UsuarioCreacion: string
  UsuarioUltimaModificacion: string
}

export const asignacionEjecutivosComercialesApi = createApi({
  reducerPath: 'asignacionEjecutivosComercialesApi',
  baseQuery,
  endpoints: (builder) => ({
    getEjecutivosComerciales: builder.query<GenericResponseOData<EjecutivoComercialAsignacion[]>, object>({
      query: () => ({
        url: '/EjecutivosComercial?$select=EjecutivoComercialId,Nombre,Apellido,CorreoElectronico,Activo,Identificador,Iniciales,NombreCompleto&$expand=AsignacionEjecutivoOperativo($select=AsignacionEjecutivoOperativoId,FechaUltimaModificacion,UsuarioUltimaModificacion;$expand=AgenteOperativo($select=AgenteOperativoId,Nombre,Apellido))&$orderby=Nombre',
        method: 'GET',
      }),
    }),
    actualizarEjecutivoComercial: builder.mutation<void, ActualizarEjecutivoComercialPayload>({
      query: (payload) => ({
        url: `/EjecutivosComercial(${payload.EjecutivoComercialId})`,
        method: 'PUT',
        body: payload,
      }),
    }),
    // Crea la relación real EjecutivoComercial <-> AgenteOperativo. No confundir con
    // AsignacionAgenteOperativoBusiness (Booking/useUpdateAgenteOperativo), que reasigna
    // el agente operativo de un Booking puntual — es un concepto distinto.
    asignarAgenteOperativo: builder.mutation<AsignacionEjecutivoOperativo, AsignarAgenteOperativoPayload>({
      query: (payload) => ({
        url: '/AsignacionesEjecutivoOperativo',
        method: 'POST',
        body: payload,
      }),
    }),
    // Actualiza la asignación existente (cambio de agente) en vez de crear una fila nueva.
    // El backend protege FechaCreacion/UsuarioIdCreacion/UsuarioCreacion (IsModified = false),
    // así que basta con mandar el Id + EjecutivoComercialId + el nuevo AgenteOperativoId.
    actualizarAsignacionAgenteOperativo: builder.mutation<void, ActualizarAsignacionAgenteOperativoPayload>({
      query: (payload) => ({
        url: `/AsignacionesEjecutivoOperativo(${payload.AsignacionEjecutivoOperativoId})`,
        method: 'PUT',
        body: payload,
      }),
    }),
    // Envía los datos del mini formulario (Tipo, Nombre, Apellido, Email, Iniciales) al
    // business endpoint. La lógica de creación real (EjecutivoComercial vs AgenteOperativo
    // según Tipo) vive en el backend (EjecutivoBusinessController -> EjecutivoSupervisor).
    crearEjecutivo: builder.mutation<void, CrearEjecutivoPayload>({
      query: (payload) => ({
        url: '/EjecutivoBusiness/CrearEjecutivo',
        method: 'POST',
        body: payload,
      }),
    }),
    // CRUD directo sobre AgenteOperativo (pestaña "Agentes Operativos"). La creación de
    // agentes NUEVOS sigue yendo por crearEjecutivo (Tipo=Operativo) porque ese flujo
    // resuelve/crea el Usuario asociado; este endpoint es solo para editar/eliminar
    // agentes que ya existen (y por lo tanto ya tienen su Usuario vinculado).
    getAgentesOperativos: builder.query<GenericResponseOData<AgenteOperativoRegistro[]>, void>({
      query: () => ({
        url: '/AgenteOperativos?$select=AgenteOperativoId,UsuarioId,Nombre,Apellido,Email,Cargo,NombreCompleto,Activo,FechaUltimaModificacion,UsuarioUltimaModificacion&$orderby=Nombre',
        method: 'GET',
      }),
    }),
    actualizarAgenteOperativo: builder.mutation<void, ActualizarAgenteOperativoPayload>({
      query: (payload) => ({
        url: `/AgenteOperativos(${payload.AgenteOperativoId})`,
        method: 'PUT',
        body: payload,
      }),
    }),
    eliminarAgenteOperativo: builder.mutation<void, number>({
      query: (agenteOperativoId) => ({
        url: `/AgenteOperativos(${agenteOperativoId})`,
        method: 'DELETE',
      }),
    }),
  }),
})

export const {
  useLazyGetEjecutivosComercialesQuery,
  useAsignarAgenteOperativoMutation,
  useActualizarAsignacionAgenteOperativoMutation,
  useActualizarEjecutivoComercialMutation,
  useCrearEjecutivoMutation,
  useLazyGetAgentesOperativosQuery,
  useActualizarAgenteOperativoMutation,
  useEliminarAgenteOperativoMutation,
} = asignacionEjecutivosComercialesApi
